import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ApiResponse } from '@/api/shared/types';
import { downloadBlob } from '@/utils/blobDownload';

import { useUI } from './useUI';


export interface UseExportConfig<TParams = void> {
  /**
   * Async function that calls the export service and returns a blob with its filename.
   * The filename is extracted from the `Content-Disposition` response header
   * by `apiClient.downloadBlobWithFilename`.
   * Does not need to be wrapped in `useCallback` — the hook stabilises it internally via a ref.
   */
  exportFn: (params: TParams) => Promise<ApiResponse<{ blob: Blob; filename: string }>>;
  /**
   * Notification message shown on success.
   * Must be a pre-evaluated string — never `() => translate(...)`.
   * Defaults to `common:messages.exportSuccess` when omitted.
   */
  successMessage?: string;
  /**
   * Notification message shown on API error or thrown exception. Pre-evaluated string.
   * Also passed as fallback to `showApiError` so it is displayed when the backend provides error.
   * Defaults to `common:messages.exportError` when omitted.
   */
  errorMessage?: string;
}

export interface UseExportResult<TParams = void> {
  /** Triggers the export. Accepts runtime params captured by `exportFn`. */
  onExport: (params: TParams) => Promise<void>;
  /** `true` while the export request is in progress. */
  isExporting: boolean;
}

/**
 * Generic hook for file export operations.
 *
 * Encapsulates the full export lifecycle: loading state, service call,
 * blob download via the server-provided filename, success/error notifications,
 * and guaranteed `isExporting` reset via `try/finally`.
 *
 * The calling hook is responsible for:
 * - Pre-validating parameters (format guards, required IDs, etc.) before calling `onExport`.
 * - Providing a domain-specific `exportFn` that wraps the service call with the current filters.
 *
 * @example — No format argument (EXCEL-only hook)
 * ```ts
 * const { onExport: exportAreas, isExporting } = useExport({
 *   exportFn: () => areaExport.export({ searchText: params.searchText }),
 *   successMessage: translate('organization:messages.exportSuccess'),
 *   errorMessage: translate('organization:messages.exportError'),
 * });
 * ```
 *
 * @example — Multi-format hook (format passed at call time)
 * ```ts
 * const { onExport: exportFile, isExporting } = useExport<UIExportFormat>({
 *   exportFn: (format) =>
 *     absenceService.exportAbsences({ ...params, format: getAPIFormat(format) }),
 *   successMessage: translate('absence:messages.exportSuccess'),
 *   errorMessage: translate('absence:messages.exportError'),
 * });
 * ```
 */
export function useExport<TParams = void>({
  exportFn,
  successMessage,
  errorMessage,
}: UseExportConfig<TParams>): UseExportResult<TParams> {
  const [isExporting, setIsExporting] = useState(false);
  const { showNotification, showApiError } = useUI();
  const { t: translate } = useTranslation();

  const resolvedSuccess = successMessage ?? translate('common:messages.exportSuccess');
  const resolvedError = errorMessage ?? translate('common:messages.exportError');

  // `exportFn` is stored in a ref so callers don't need to wrap it in `useCallback`.
  // Assigning during render keeps the ref current before any async callback can fire.
  const exportFnRef = useRef(exportFn);
  exportFnRef.current = exportFn;

  const onExport = useCallback(async (params: TParams) => {
    setIsExporting(true);
    try {
      const response = await exportFnRef.current(params);
      if (response.success && response.data) {
        downloadBlob(response.data.blob, response.data.filename);
        showNotification('success', resolvedSuccess, 5000);
      } else {
        showApiError(response, resolvedError);
      }
    } catch {
      showNotification('error', resolvedError, 5000);
    } finally {
      setIsExporting(false);
    }
    // `exportFnRef` intentionally excluded — stable ref object, not a reactive value.
  }, [resolvedSuccess, resolvedError, showNotification, showApiError]);

  return { onExport, isExporting };
}
